import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

const ImageViewRevenuePanel = () => {
  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('today');
  const [topImages, setTopImages] = useState([]);
  const [topUsers, setTopUsers] = useState([]);

  useEffect(() => {
    loadRevenueData();
  }, [timeRange]);

  const loadRevenueData = async () => {
    try {
      setLoading(true);
      
      // โหลดข้อมูลผู้ใช้ทั้งหมด
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      
      let totalRevenue = 0;
      let totalViews = 0;
      let totalPurchases = 0;
      const userStats = [];
      const imageStats = {};
      
      const now = new Date();
      let startDate;
      
      switch (timeRange) {
        case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case 'week':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case 'all':
          startDate = new Date(0);
          break;
      }

      usersSnapshot.docs.forEach(doc => {
        const userData = doc.data();
        const userId = doc.id;
        
        // คำนวณรายได้จากการซื้อสิทธิ์เพิ่มเติม
        const purchaseHistory = userData.purchaseHistory || [];
        const periodPurchases = purchaseHistory.filter(purchase => 
          purchase.type === 'additionalViews' && 
          new Date(purchase.timestamp) >= startDate
        );
        
        const userRevenue = periodPurchases.reduce((sum, purchase) => sum + purchase.price, 0);
        totalRevenue += userRevenue;
        totalPurchases += periodPurchases.length;
        
        // คำนวณจำนวนการดูรูป
        const viewedImages = userData.viewedImages || [];
        const periodViews = viewedImages.filter(view => 
          new Date(view.timestamp) >= startDate
        );
        
        totalViews += periodViews.length;
        
        if (userRevenue > 0 || periodViews.length > 0) {
          userStats.push({
            userId,
            name: userData.displayName || userData.nickname || userData.email,
            role: userData.role,
            subscription: userData.subscription,
            revenue: userRevenue,
            views: periodViews.length,
            purchases: periodPurchases.length
          });
        }
        
        // รวบรวมสถิติรูปภาพ
        periodViews.forEach(view => {
          if (!imageStats[view.imageId]) {
            imageStats[view.imageId] = {
              imageId: view.imageId,
              girlId: view.girlId,
              views: 0,
              revenue: 0
            };
          }
          imageStats[view.imageId].views++;
        });
      });

      // เรียงลำดับผู้ใช้ตามรายได้
      userStats.sort((a, b) => b.revenue - a.revenue);
      setTopUsers(userStats.slice(0, 10));

      // เรียงลำดับรูปภาพตามจำนวนการดู
      const imageStatsArray = Object.values(imageStats);
      imageStatsArray.sort((a, b) => b.views - a.views);
      setTopImages(imageStatsArray.slice(0, 10));

      setRevenueData({
        totalRevenue,
        totalViews,
        totalPurchases,
        userStats,
        imageStats: imageStatsArray
      });

    } catch (error) {
      console.error('Error loading revenue data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTimeRangeDisplayName = (range) => {
    const names = {
      today: 'วันนี้',
      week: 'สัปดาห์นี้',
      month: 'เดือนนี้',
      all: 'ทั้งหมด'
    };
    return names[range] || range;
  };

  const getRoleDisplayName = (role) => {
    const names = {
      customer: 'ลูกค้า',
      girl: 'น้อง',
      momo: 'มอม',
      admin: 'แอดมิน'
    };
    return names[role] || role;
  };

  const getSubscriptionDisplayName = (subscription) => {
    const names = {
      free: 'Free',
      basic: 'Basic',
      silver: 'Silver',
      gold: 'Gold',
      platinum: 'Platinum',
      premium: 'Premium',
      vip: 'VIP',
      starter: 'Starter',
      pro: 'Pro',
      enterprise: 'Enterprise',
      ultraEnterprise: 'Ultra Enterprise'
    };
    return names[subscription] || subscription;
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow border">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">💰 รายได้จากการดูรูปภาพ</h2>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="today">วันนี้</option>
          <option value="week">สัปดาห์นี้</option>
          <option value="month">เดือนนี้</option>
          <option value="all">ทั้งหมด</option>
        </select>
      </div>

      {/* สถิติหลัก */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center">
            <span className="text-green-600 text-3xl mr-3">💰</span>
            <div>
              <div className="text-3xl font-bold text-green-900">
                ฿{revenueData?.totalRevenue || 0}
              </div>
              <div className="text-green-700">รายได้รวม</div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center">
            <span className="text-blue-600 text-3xl mr-3">👁️</span>
            <div>
              <div className="text-3xl font-bold text-blue-900">
                {revenueData?.totalViews || 0}
              </div>
              <div className="text-blue-700">การดูรูปรวม</div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6">
          <div className="flex items-center">
            <span className="text-purple-600 text-3xl mr-3">🛒</span>
            <div>
              <div className="text-3xl font-bold text-purple-900">
                {revenueData?.totalPurchases || 0}
              </div>
              <div className="text-purple-700">การซื้อสิทธิ์</div>
            </div>
          </div>
        </div>
      </div>

      {/* Top Users */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-xl font-semibold mb-4">🏆 ผู้ใช้ที่สร้างรายได้สูงสุด</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4">อันดับ</th>
                <th className="text-left py-3 px-4">ชื่อ</th>
                <th className="text-left py-3 px-4">Role</th>
                <th className="text-left py-3 px-4">Package</th>
                <th className="text-left py-3 px-4">รายได้</th>
                <th className="text-left py-3 px-4">การดูรูป</th>
                <th className="text-left py-3 px-4">การซื้อ</th>
              </tr>
            </thead>
            <tbody>
              {topUsers.map((user, index) => (
                <tr key={user.userId} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                      index === 0 ? 'bg-yellow-100 text-yellow-800' :
                      index === 1 ? 'bg-gray-100 text-gray-800' :
                      index === 2 ? 'bg-orange-100 text-orange-800' :
                      'bg-gray-50 text-gray-600'
                    }`}>
                      #{index + 1}
                    </span>
                  </td>
                  <td className="py-3 px-4 font-medium">{user.name}</td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                      {getRoleDisplayName(user.role)}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                      {getSubscriptionDisplayName(user.subscription)}
                    </span>
                  </td>
                  <td className="py-3 px-4 font-semibold text-green-600">
                    ฿{user.revenue}
                  </td>
                  <td className="py-3 px-4">{user.views} ครั้ง</td>
                  <td className="py-3 px-4">{user.purchases} ครั้ง</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Images */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-xl font-semibold mb-4">📸 รูปภาพที่ได้รับความนิยม</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4">อันดับ</th>
                <th className="text-left py-3 px-4">รูปภาพ</th>
                <th className="text-left py-3 px-4">น้อง</th>
                <th className="text-left py-3 px-4">จำนวนการดู</th>
                <th className="text-left py-3 px-4">รายได้</th>
              </tr>
            </thead>
            <tbody>
              {topImages.map((image, index) => (
                <tr key={image.imageId} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                      index === 0 ? 'bg-yellow-100 text-yellow-800' :
                      index === 1 ? 'bg-gray-100 text-gray-800' :
                      index === 2 ? 'bg-orange-100 text-orange-800' :
                      'bg-gray-50 text-gray-600'
                    }`}>
                      #{index + 1}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-2">
                      <img
                        src={`https://firebasestorage.googleapis.com/v0/b/hugkan-8dd6b.appspot.com/o/images%2F${image.girlId}%2F${image.imageId}%2Fthumbnail.jpg?alt=media`}
                        alt="Thumbnail"
                        className="w-10 h-10 object-cover rounded"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMCAxMEMyMy4zMTM3IDEwIDI2IDEyLjY4NjMgMjYgMTZDMjYgMTkuMzEzNyAyMy4zMTM3IDIyIDIwIDIyQzE2LjY4NjMgMjIgMTQgMTkuMzEzNyAxNCAxNkMxNCAxMi42ODYzIDE2LjY4NjMgMTAgMjAgMTBaIiBmaWxsPSIjOUNBM0FGIi8+CjxwYXRoIGQ9Ik0xMCAzMEgzMFYzMkgzMlYzMEgxMlYzMkgxMFYzMFoiIGZpbGw9IiM5Q0EzQUYiLz4KPC9zdmc+';
                        }}
                      />
                      <span className="text-sm text-gray-600">ID: {image.imageId.substring(0, 8)}...</span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-1 bg-pink-100 text-pink-800 rounded-full text-sm">
                      {image.girlId.substring(0, 8)}...
                    </span>
                  </td>
                  <td className="py-3 px-4 font-semibold">{image.views} ครั้ง</td>
                  <td className="py-3 px-4 font-semibold text-green-600">
                    ฿{image.revenue || 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* สรุปข้อมูล */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-xl font-semibold mb-4">📊 สรุปข้อมูล</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {revenueData?.userStats.length || 0}
            </div>
            <div className="text-sm text-gray-600">ผู้ใช้ที่ใช้งาน</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {revenueData?.imageStats.length || 0}
            </div>
            <div className="text-sm text-gray-600">รูปภาพที่ถูกดู</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {revenueData?.totalRevenue ? Math.round(revenueData.totalRevenue / revenueData.totalViews * 100) / 100 : 0}
            </div>
            <div className="text-sm text-gray-600">รายได้ต่อการดู</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {revenueData?.totalPurchases ? Math.round(revenueData.totalViews / revenueData.totalPurchases * 100) / 100 : 0}
            </div>
            <div className="text-sm text-gray-600">การดูต่อการซื้อ</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageViewRevenuePanel;









